import BorderClearIcon from "@mui/icons-material/BorderClear";
import FullscreenSharpIcon from "@mui/icons-material/FullscreenSharp";
import React, { ReactElement, useState } from "react";

import { RenderingObjectKind } from "../../../generated/gql/graphql";
import useLocale from "../../../hooks/use-locale";
import { Size } from "../../../types/size";
import { Vector2 } from "../../../types/vector";
import { classNames } from "../../../utils/classes";
import SideNavigation from "../../button/side-navigation";
import AspectRatioOverlay from "../../composer/aspect-ratio-overlay";
import BackgroundAspectRatioActions from "../../composer/background-aspect-ratio-actions";
import BackgroundElement from "../../composer/background-element";
import BackgroundGridActions from "../../composer/background-grid-actions";
import BackgroundZoom from "../../composer/background-zoom";
import CanvasGraphicElement from "../../graphics/canvas-graphic-element";
import { CanvasTextElement } from "../../texts/canvas-text-element";

import { CanvasElementProps } from "./canvas-element";
import { CanvasObject } from "./canvas-object";
import PackshotElement from "./packshot-element";
import SaveAsTemplateButton from "./save-as-template-button";

export type LayoutEditorProps = {
  /** Layout name */
  layoutName: string;

  /** Background id */
  backgroundId: string | undefined;

  /** Background scale */
  scale: number;
  scaleChange: (value: number) => void;
  scaleChangeEnd: (value: number) => void;

  /** Background offset */
  offset: Vector2;
  offsetChange: (offset: Vector2) => void;

  scaleOffsetChange: (scale: number, offset: Vector2) => void;

  /** Objects */
  canvasObjects: CanvasObject[];
  selectedCanvasObjectIndex: number;
  objectChanged: (
    index: number,
    kind: RenderingObjectKind,
    changes: Partial<CanvasObject>,
  ) => void;
  objectDeleted: (index: number) => void;
  objectDuplicated: (index: number) => void;
  objectMoved: (index: number, newIndex: number) => void;
  objectSelected: (index: number) => void;

  /** Indicates if layout can be edited */
  locked: boolean;

  /** Layout aspect ratio */
  aspectRatio: number | null;
  aspectRatioChange: (aspectRatio: number | null) => void;

  /** Final aspect ratio to use */
  finalAspectRatio: number;

  /** Canvas size (in px) */
  canvasSize: Size;
};

export default function LayoutEditor(props: LayoutEditorProps): ReactElement {
  const {
    layoutName,
    backgroundId,
    scale,
    scaleChange,
    scaleChangeEnd,
    offset,
    offsetChange,
    scaleOffsetChange,
    canvasObjects,
    selectedCanvasObjectIndex,
    objectChanged,
    objectDuplicated,
    objectDeleted,
    objectMoved,
    objectSelected,
    locked,
    aspectRatio,
    aspectRatioChange,
    finalAspectRatio,
    canvasSize,
  } = props;

  const { getText } = useLocale();
  const [leftSideNavigationExpanded, setLeftSideNavigationExpanded] =
    useState<boolean>(false);
  const [rightSideNavigationExpanded, setRightSideNavigationExpanded] =
    useState<boolean>(false);

  return (
    <div
      className={classNames(
        "relative mb-6 ml-5 mr-5 flex",
        "items-center justify-center",
        `${backgroundId ? "rounded-none" : "rounded-2xl bg-companyLightGrey"}`,
      )}
      style={{
        width: `${canvasSize.width}px`,
        height: `${canvasSize.height}px`,
      }}
    >
      <div className="absolute left-[-78px] top-0 flex flex-col gap-2">
        <SideNavigation
          expanded={leftSideNavigationExpanded}
          setExpanded={setLeftSideNavigationExpanded}
          icon={<BorderClearIcon />}
          className="!relative !left-0 !top-0"
          disabled={!backgroundId}
        >
          <BackgroundGridActions />
          <BackgroundAspectRatioActions
            aspectRatio={aspectRatio}
            setAspectRatio={aspectRatioChange}
          />
        </SideNavigation>
        <SaveAsTemplateButton
          layoutName={layoutName}
          backgroundId={backgroundId}
          scale={scale}
          offset={offset}
          canvasObjects={canvasObjects}
        />
      </div>
      <SideNavigation
        expanded={rightSideNavigationExpanded}
        setExpanded={setRightSideNavigationExpanded}
        icon={<FullscreenSharpIcon />}
        position="right-[-78px]"
        disabled={!backgroundId}
      >
        <BackgroundZoom
          scale={scale}
          scaleChange={scaleChange}
          scaleChangeEnd={scaleChangeEnd}
          scaleOffsetChange={scaleOffsetChange}
        />
      </SideNavigation>
      {backgroundId ? (
        <div className="relative h-full w-full overflow-hidden bg-companyMediumGrey">
          <AspectRatioOverlay
            aspectRatio={finalAspectRatio}
            canvasSize={canvasSize}
          >
            {backgroundId && (
              <BackgroundElement
                key={backgroundId}
                backgroundId={backgroundId}
                isDraggable={rightSideNavigationExpanded}
                scale={scale}
                offset={offset}
                offsetChange={offsetChange}
                canvasSize={canvasSize}
                objectSelected={objectSelected}
              />
            )}
            {canvasObjects.map((object, index) => {
              const canvasElementProps: Omit<
                CanvasElementProps,
                "canvasObject" | "canvasObjectSize" | "imageUrl"
              > = {
                index,
                objectChanged: (changes) =>
                  objectChanged(index, object.kind, changes),
                objectDeleted: () => objectDeleted(index),
                objectDuplicated: () => objectDuplicated(index),
                objectMovedDown: () => objectMoved(index, index - 1),
                objectMovedUp: () => objectMoved(index, index + 1),
                objectSelected: () => objectSelected(index),
                isGenerated: locked,
                selected: index === selectedCanvasObjectIndex,
                canvasSize: canvasSize,
              };
              switch (object.kind) {
                case RenderingObjectKind.Presentation:
                  return (
                    <PackshotElement
                      key={index}
                      packshot={object}
                      {...canvasElementProps}
                    />
                  );
                case RenderingObjectKind.Graphic:
                  return (
                    // Show canvas graphic element
                    <CanvasGraphicElement
                      key={index}
                      graphic={object}
                      {...canvasElementProps}
                    />
                  );
                case RenderingObjectKind.Text:
                  return (
                    // Show canvas text element
                    <CanvasTextElement
                      key={index}
                      text={object}
                      {...canvasElementProps}
                    />
                  );
              }
            })}
          </AspectRatioOverlay>
        </div>
      ) : (
        <p className="m-auto px-5 text-center text-xl text-companyMediumGrey">
          {getText("sections.canvas.placeholder")}
        </p>
      )}
    </div>
  );
}
